import {
  createContext,
  useEffect,
  useReducer,
  ReactElement,
  useState,
  useContext,
} from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import {
  getDownloadURL,
  uploadBytesResumable,
  ref,
  getStorage,
} from "firebase/storage";
import { LOGIN, LOGOUT } from "store/reducers/actions";
import authReducer from "store/reducers/auth";

import Loader from "components/Loader";
import { AuthProps, FirebaseContextType } from "types/auth";
import serverCall, { Token } from "api/serverCall";
import api from "api/api";
import { IUser } from "types/user";
import { FORM_VALIDATION_MESSAGES } from "constants/messages";

let storage: any = null;
if (!firebase.apps.length) {
  const app = firebase.initializeApp({
    apiKey: "AIzaSyDb0uamoRhW-MDbFU5xHWIbrEF7ZepG0Xo",
    authDomain: "mimic-1decc.firebaseapp.com",
    projectId: "mimic-1decc",
    storageBucket: "mimic-1decc.firebasestorage.app",
    messagingSenderId: "134668996",
    appId: "1:134668996:web:70e79a12c82e0c43bb1f91",
    measurementId: "G-GSYTJHQ246",
  });
  storage = getStorage(app);
}

// const
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
};

export const getFirebaseErrorMessage = (error: any): string => {
  switch (error.code) {
    case "auth/user-not-found":
      return "User not found. Please check your email or sign up.";
    case "auth/wrong-password":
      return "Invalid password. Please try again.";
    case "auth/email-already-in-use":
      return "The email address is already in use by another account.";
    case "auth/weak-password":
      return "The password is too weak. Please choose a stronger password.";
    case "auth/invalid-email":
      return "Invalid email address. Please enter a valid email.";
    case "auth/invalid-login-credentials":
      return "Invalid login credentials. Please check your email and password.";
    case "auth/user-disabled":
      return "Unable to access account. Please contact administrator.";
    default:
      return FORM_VALIDATION_MESSAGES.SOMETHING_WENT_WRONG;
  }
};

// ==============================|| FIREBASE CONTEXT & PROVIDER ||============================== //

const FirebaseContext = createContext<FirebaseContextType | null>(null);

export const FirebaseProvider = ({ children }: { children: ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [authServerError, setAuthServerError] = useState<any>(null);

  useEffect(() => {
    setAuthServerError(null);
    firebase.auth().onAuthStateChanged((user: any) => {
      if (user) {
        try {
          user.getIdToken().then((idToken: string) => {
            setAuthServerError(null);
            getUserFromServer(idToken);
          });
        } catch (error) {
          console.error(error);
        }
      } else {
        dispatch({
          type: LOGOUT,
        });
      }
    });
  }, [dispatch]);

  async function getUserFromServer(idToken: string) {
    try {
      Token.token = idToken;
      const response = await serverCall.get<{ admin: IUser }>(api.login());
      setAuthServerError(null);

      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: response?.admin,
        },
      });
    } catch (error: any) {
      logout();
      setAuthServerError(error?.response?.data ?? "Something went wrong");
    }
  }

  const firebaseEmailPasswordSignIn = (email: string, password: string) =>
    firebase.auth().signInWithEmailAndPassword(email, password);

  const firebaseGoogleSignIn = () => {
    const provider = new firebase.auth.GoogleAuthProvider();
    return firebase.auth().signInWithPopup(provider);
  };

  const firebaseTwitterSignIn = () => {
    const provider = new firebase.auth.TwitterAuthProvider();
    return firebase.auth().signInWithPopup(provider);
  };

  const firebaseFacebookSignIn = () => {
    const provider = new firebase.auth.FacebookAuthProvider();
    return firebase.auth().signInWithPopup(provider);
  };

  const firebaseRegister = async (email: string, password: string) =>
    firebase.auth().createUserWithEmailAndPassword(email, password);

  const logout = () => firebase.auth().signOut();

  const resetPassword = async (email: string) => {
    await firebase.auth().sendPasswordResetEmail(email);
  };

  const updateProfile = async () => {};

  const uploadProfilePicture = async (data: any): Promise<string | null> => {
    try {
      const storageRef = ref(storage, data.name);
      const uploadTask = uploadBytesResumable(storageRef, data);
      const snapshot = await uploadTask;

      const downloadedURL = await getDownloadURL(snapshot.ref);
      return downloadedURL;
    } catch (error) {
      console.error("Error uploading data: ", error);
      return null;
    }
  };

  if (
    state.isInitialized !== undefined &&
    !state.isInitialized &&
    !authServerError
  ) {
    return <Loader />;
  }

  return (
    <FirebaseContext.Provider
      value={{
        ...state,
        firebaseRegister,
        firebaseEmailPasswordSignIn,
        login: () => {},
        firebaseGoogleSignIn,
        firebaseTwitterSignIn,
        firebaseFacebookSignIn,
        logout,
        resetPassword,
        updateProfile,
        uploadProfilePicture,
        authServerError,
      }}
    >
      {children}
    </FirebaseContext.Provider>
  );
};

export const useFirebaseContext = () => {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error("useFirebase must be used within a FirebaseProvider");
  }
  return context;
};

export default FirebaseContext;
